import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Plus, Sparkles, TrendingUp, BookOpen, Clock, ChevronRight, BarChart3 } from 'lucide-react';
import { getDreams, getStats, getMe } from '../lib/api';
import DreamCard from '../components/DreamCard';

const ONIRVA_ICON = "https://customer-assets.emergentagent.com/job_200ec0e0-0820-4417-b1b5-b6286eb18bc4/artifacts/c3nyk9fu_80AC8250-CA42-4BF9-BA0D-F1211AA6A764.png";

export default function Dashboard() {
  const navigate = useNavigate();
  const [dreams, setDreams] = useState([]);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [subscription, setSubscription] = useState(null);
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [dreamsRes, statsRes, meRes] = await Promise.all([
          getDreams(5),
          getStats(),
          getMe()
        ]);
        setDreams(dreamsRes.data);
        setStats(statsRes.data);
        setUser(meRes.data);
        setSubscription(meRes.data.subscription);
        localStorage.setItem('onirva_user', JSON.stringify(meRes.data));
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };
  
  return (
    <div className="min-h-screen bg-background pt-20 pb-8" data-testid="dashboard-page">
      <div className="hero-gradient absolute inset-0" />
      
      <div className="relative z-10">
        {/* Header */}
        <header className="p-6">
          <h1 className="text-2xl font-serif font-bold mb-1">
            {getGreeting()}, {user?.name?.split(' ')[0] || 'Dreamer'}
          </h1>
          <p className="text-muted-foreground">Whenever you're ready, begin with a dream.</p>
        </header>
        
        {/* Trial Banner */}
        {subscription?.status === 'trial' && (
          <div className="px-6 mb-4">
            <div 
              className="glass-card p-3 bg-gradient-to-r from-primary/20 to-violet-500/20 border-primary/30 cursor-pointer"
              onClick={() => navigate('/subscription')}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-primary" />
                  <div>
                    <span className="text-sm font-medium">Free Trial</span>
                    <span className="text-sm text-muted-foreground ml-2">
                      {subscription.trial_days_remaining} days left
                    </span>
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </div>
            </div>
          </div>
        )}
        
        {/* Free Tier Analysis Limit */}
        {subscription?.tier === 'free' && subscription?.analyses_remaining !== undefined && (
          <div className="px-6 mb-4">
            <div 
              className="glass-card p-3 cursor-pointer"
              onClick={() => navigate('/subscription')}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Sparkles className="w-5 h-5 text-primary" />
                  <span className="text-sm">
                    {subscription.analyses_remaining} AI analyses remaining this month
                  </span>
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground" />
              </div>
            </div>
          </div>
        )}
        
        {/* Quick Record Button */}
        <div className="px-6 mb-8">
          <Button 
            className="w-full rounded-2xl py-8 glow-primary hover:scale-[1.02] transition-transform"
            onClick={() => navigate('/capture')}
            data-testid="record-dream-btn"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center">
                <Plus className="w-6 h-6" />
              </div>
              <div className="text-left">
                <div className="font-semibold">Record a Dream</div>
                <div className="text-sm opacity-80">Capture your dream before it fades</div>
              </div>
            </div>
          </Button>
        </div>
        
        {/* Stats Cards */}
        {stats && (
          <div className="px-6 mb-8">
            <div className="grid grid-cols-2 gap-4">
              <div className="glass-card p-4">
                <div className="flex items-center gap-2 text-muted-foreground mb-2">
                  <BookOpen className="w-4 h-4" />
                  <span className="text-xs uppercase tracking-wider">Dreams</span>
                </div>
                <div className="text-2xl font-serif font-bold">{stats.total_dreams}</div>
              </div>
              
              <div 
                className="glass-card p-4 cursor-pointer hover:border-primary/30 transition-colors"
                onClick={() => navigate('/analytics')}
              >
                <div className="flex items-center gap-2 text-muted-foreground mb-2">
                  <BarChart3 className="w-4 h-4" />
                  <span className="text-xs uppercase tracking-wider">Analytics</span>
                </div>
                <div className="text-2xl font-serif font-bold">{stats.analyzed_dreams}</div>
              </div>
            </div>
            
            {/* Top Symbols */}
            {stats.top_symbols?.length > 0 && (
              <div 
                className="glass-card p-4 mt-4 cursor-pointer hover:border-primary/30 transition-colors"
                onClick={() => navigate('/intelligence')}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Sparkles className="w-4 h-4 text-primary" />
                    <span className="text-xs uppercase tracking-wider">Dream Intelligence</span>
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                </div>
                <div className="flex flex-wrap gap-2">
                  {stats.top_symbols.map((item, index) => (
                    <span 
                      key={index}
                      className="px-3 py-1 rounded-full bg-primary/20 text-primary text-sm"
                    >
                      {item.symbol} ({item.count})
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
        
        {/* Recent Dreams */}
        <div className="px-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-serif font-bold">Recent Dreams</h2>
            {dreams.length > 0 && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/journal')}
                className="text-primary"
              >
                View all
              </Button>
            )}
          </div>
          
          {isLoading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="glass-card p-4 h-24 skeleton" />
              ))}
            </div>
          ) : dreams.length === 0 ? (
            <div className="glass-card p-8 text-center">
              <img src={ONIRVA_ICON} alt="" className="w-16 h-16 mx-auto mb-4 opacity-50 rounded-xl" />
              <h3 className="font-serif font-bold mb-2">No dreams recorded yet</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Your dreams will appear here over time.
              </p>
              <Button 
                variant="outline" 
                className="rounded-full"
                onClick={() => navigate('/capture')}
              >
                <Plus className="w-4 h-4 mr-2" />
                Record Dream
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {dreams.map((dream) => (
                <DreamCard 
                  key={dream.dream_id} 
                  dream={dream} 
                  onDelete={(deletedId) => {
                    setDreams(prev => prev.filter(d => d.dream_id !== deletedId));
                    setStats(prev => prev ? {...prev, total_dreams: prev.total_dreams - 1} : prev);
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
